import {
  ICommunicationHistory,
  CommunicationChannelFilter,
  CommunicationItemType,
} from '@dataTypes/communicationHistory.type'

const CHANNEL_TO_TYPE: Record<
  Exclude<CommunicationChannelFilter, CommunicationChannelFilter.All>,
  CommunicationItemType
> = {
  [CommunicationChannelFilter.Calls]: CommunicationItemType.Calls,
  [CommunicationChannelFilter.SMS]: CommunicationItemType.SMS,
  [CommunicationChannelFilter.Email]: CommunicationItemType.Email,
  [CommunicationChannelFilter.Fax]: CommunicationItemType.Fax,
}

// Format to "Feb 28, 2024 - 9:20 AM"
const formatTimestamp = (value: string): string => {
  const date = new Date(value)
  if (isNaN(date.getTime())) return value
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${datePart} - ${timePart}`
}

const buildPartyLine = (item: ICommunicationHistory): string => {
  const prefix = item.direction === 'Outbound' ? 'To' : 'From'
  return `${prefix}: ${item.patientName}`
}

const sortByCreatedDesc = (
  items: ICommunicationHistory[],
): ICommunicationHistory[] => {
  return [...items].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  )
}

// Most recent `n` logs across all channels.
export const recentAll = (
  items: ICommunicationHistory[],
  n = 9,
): ICommunicationHistory[] => {
  return sortByCreatedDesc(items).slice(0, n)
}

// Most recent `n` logs for a single channel.
export const recentPerChannel = (
  items: ICommunicationHistory[],
  channel: CommunicationItemType,
  n = 9,
): ICommunicationHistory[] => {
  return recentAll(
    items.filter((item) => item.type === channel),
    n,
  )
}

// Recent logs for the active tab
export const getRecentHistoryItems = (
  items: ICommunicationHistory[],
  channel: CommunicationChannelFilter,
  n = 9,
): ICommunicationHistory[] => {
  const recent =
    channel === CommunicationChannelFilter.All
      ? recentAll(items, n)
      : recentPerChannel(items, CHANNEL_TO_TYPE[channel], n)
  return recent.map((r) => ({
    ...r,
    patientName: buildPartyLine(r),
    timestamp: formatTimestamp(r.timestamp),
  }))
}

// Format title
const ITEM_TYPE_LABEL: Record<CommunicationItemType, string> = {
  [CommunicationItemType.Calls]: 'Call',
  [CommunicationItemType.SMS]: 'SMS',
  [CommunicationItemType.Email]: 'Email',
  [CommunicationItemType.Fax]: 'Fax',
}

export const formatItemTitle = (item: ICommunicationHistory): string => {
  return `${ITEM_TYPE_LABEL[item.type] ?? item.type} (${item.direction})`
}
